import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/context/AuthContext';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/ui/use-toast";
import { Palette, Type, Image, Layout, Mail, Code, Upload, X, Smartphone, Monitor, Save, Pencil, FileArchive, Loader2 } from 'lucide-react';
import { getTextColorForBackground } from '@/lib/core/utils';
import EmailTemplatePreview from './EmailTemplatePreview';
import { CodeEditor } from '../ui/code-editor';
import { extractCanvaZip } from '@/lib/email/canva-zip-handler';
import { parseCanvaHtml, type TextBlock, type Button as ParsedButton } from '@/lib/email/canva-html-parser';
import { generateCanvaTemplate, type CanvaTemplateMappings } from '@/lib/email/canva-template-generator';
import { CanvaTemplateMappingDialog } from './CanvaTemplateMappingDialog';
import { PersonalizationToolbar } from './PersonalizationToolbar';

interface EmailTemplate {
  id: string;
  name: string;
  configuration: {
    preset?: string;
    logoUrl?: string;
    logoPosition?: 'left' | 'center' | 'right';
    primaryColor?: string;
    fontFamily?: string;
    headerText?: string;
    preheaderText?: string; // Preview text shown in email clients before opening
    mainMessage?: string;
    ctaText?: string;
    ctaLink?: string;
    buttonType?: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none';
    selectedReviewId?: string;
    selectedCouponId?: string;
    selectedLoyaltyId?: string;
    footerText?: string;
    unsubscribeText?: string;
    html?: string;
    importSource?: 'canva' | 'canva_app' | 'custom';
    // Canva App import fields
    canvaHeaderImage?: string;  // URL to uploaded Canva design image
    canvaBlocks?: Array<{
      id: string;
      type: 'editable_text' | 'cta_button';
      content?: string;
      label?: string;
      buttonType?: string;
      ctaUrl?: string;
    }>;
  };
  user_id: string;
  created_at: string;
}

const EMAIL_PRESETS = [
  { id: 'welcome', name: 'Welcome Email', description: 'Welcome new customers' },
  { id: 'loyalty', name: 'Loyalty', description: 'Invite customers to join your rewards program' },
  { id: 'coupon-reward', name: 'Coupon Reward', description: 'Send digital offers to customers' },
  { id: 'event-invitation', name: 'Event Invitation', description: 'Invite customers to events' },
  { id: 'newsletter', name: 'Newsletter', description: 'Regular business updates' },
  { id: 'promotion', name: 'Promotion', description: 'Special offers and deals' },
];

const FONT_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
];

const getPresetContent = (presetId: string) => {
  switch (presetId) {
    case 'welcome':
      return {
        headerText: 'Welcome to Our Community!',
        mainMessage: 'Thank you for joining us! We\'re excited to share updates and exclusive content with you.',
        ctaText: 'Get Started',
        footerText: 'Best regards, Your Team'
      };
    case 'loyalty':
      return {
        headerText: 'Join Our Rewards Program!',
        mainMessage: 'Be part of our exclusive rewards program and enjoy special perks, discounts, and member-only benefits.',
        ctaText: 'Get My Card',
        footerText: 'Start earning rewards with your first visit!'
      };
    case 'coupon-reward':
      return {
        headerText: 'Special Offer Just for You!',
        mainMessage: 'We have an exclusive offer waiting for you! Add this special deal to your phone and use it on your next visit.',
        ctaText: 'Get Offer',
        footerText: 'Valid until end of month. Terms and conditions apply.'
      };
    case 'event-invitation':
      return {
        headerText: 'You\'re Invited!',
        mainMessage: 'Join us for our upcoming special event. Don\'t miss out on this exclusive opportunity.',
        ctaText: 'RSVP Now',
        footerText: 'We hope to see you there!'
      };
    case 'newsletter':
      return {
        headerText: 'Monthly Newsletter',
        mainMessage: 'Stay updated with our latest news, updates, and special offers. Here\'s what\'s happening this month.',
        ctaText: 'Read More',
        footerText: 'Thank you for subscribing to our newsletter.'
      };
    case 'promotion':
      return {
        headerText: 'Limited Time Offer!',
        mainMessage: 'Don\'t miss out on our special promotion! Save big on your favorite products for a limited time.',
        ctaText: 'Shop Now',
        footerText: 'Offer valid while supplies last.'
      };
    default:
      return {
        headerText: 'Welcome to Our Newsletter',
        mainMessage: 'Thank you for joining our community! We\'re excited to share updates and exclusive content with you.',
        ctaText: 'Learn More',
        footerText: 'Best regards, Your Team'
      };
  }
};

interface EmailTemplateBuilderProps {
  businessId: string | null;
  existingTemplate?: EmailTemplate | null;
  onComplete: () => void;
  onCancel: () => void;
}

const EmailTemplateBuilder: React.FC<EmailTemplateBuilderProps> = ({
  businessId,
  existingTemplate,
  onComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const { selectedBusinessId: contextBusinessId, loadingBusinesses } = useBusinesses();

  // Use provided businessId or fallback to context businessId
  const selectedBusinessId = businessId || contextBusinessId;

  const DRAFT_KEY = `emailTemplateDraft_${user?.id}_${existingTemplate?.id || 'new'}`;

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };

  const draft = loadDraft();

  const [currentTemplate, setCurrentTemplate] = useState<Partial<EmailTemplate>>(() => {
    if (draft?.currentTemplate) return draft.currentTemplate;
    if (existingTemplate) return { name: existingTemplate.name, configuration: existingTemplate.configuration };

    const welcomeContent = getPresetContent('welcome');
    return {
      name: '',
      configuration: {
        preset: 'welcome',
        primaryColor: '#1976d2',
        fontFamily: 'Arial, sans-serif',
        logoPosition: 'center',
        ...welcomeContent,
        ctaLink: 'https://example.com',
        buttonType: 'review',
      }
    };
  });

  const [isAdvancedMode, setIsAdvancedMode] = useState(() =>
    draft?.isAdvancedMode !== undefined
      ? draft.isAdvancedMode
      : existingTemplate ? (!!existingTemplate.configuration.html && !existingTemplate.configuration.preset) : false
  );

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ currentTemplate, isAdvancedMode, timestamp: Date.now() }));
    } catch {}
  }, [currentTemplate, isAdvancedMode, DRAFT_KEY]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  };

  const handleModeToggle = () => {
    if (!isAdvancedMode) {
      setCurrentTemplate({
        ...currentTemplate,
        configuration: { ...currentTemplate.configuration, html: generateHTML(currentTemplate.configuration) }
      });
    }
    setIsAdvancedMode(!isAdvancedMode);
  };
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>(
    existingTemplate?.configuration?.logoUrl && !existingTemplate.configuration.logoUrl.startsWith('data:')
      ? existingTemplate.configuration.logoUrl : ''
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editedMainMessage, setEditedMainMessage] = useState('');
  const [savedMainMessage, setSavedMainMessage] = useState('');

  const canvaZipInputRef = useRef<HTMLInputElement>(null);
  const mainMessageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isImportingCanva, setIsImportingCanva] = useState(false);
  const [canvaMappingDialogOpen, setCanvaMappingDialogOpen] = useState(false);
  const [canvaTextBlocks, setCanvaTextBlocks] = useState<TextBlock[]>([]);
  const [canvaButtons, setCanvaButtons] = useState<ParsedButton[]>([]);
  const [canvaHtmlPreview, setCanvaHtmlPreview] = useState('');

  const [reviewForms, setReviewForms] = useState<{id: string, title: string, active_status: boolean}[]>([]);
  const [couponOffers, setCouponOffers] = useState<{id: string, name: string, is_active: boolean}[]>([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<{id: string, name: string, is_active: boolean, template_code?: string}[]>([]);

  useEffect(() => {
    if (!selectedBusinessId || loadingBusinesses) return;

    Promise.all([
      supabase.from('review_forms').select('id, title').eq('business_id', selectedBusinessId).eq('active_status', true).order('created_at', { ascending: false }),
      supabase.from('coupon_offers').select('id, name, is_active').eq('business_id', selectedBusinessId).is('deleted_at', null),
      supabase.from('loyalty_programs').select('id, name, is_active, template_code').eq('business_id', selectedBusinessId).is('deleted_at', null)
    ]).then(([formsRes, couponsRes, loyaltyRes]) => {
      if (formsRes.data) setReviewForms(formsRes.data.map(f => ({ ...f, active_status: true })));
      if (couponsRes.data) setCouponOffers(couponsRes.data);
      if (loyaltyRes.data) setLoyaltyPrograms(loyaltyRes.data);
    });
  }, [selectedBusinessId, loadingBusinesses]);

  useEffect(() => {
    if (currentTemplate.configuration?.mainMessage !== undefined) {
      setEditedMainMessage(currentTemplate.configuration.mainMessage);
      setSavedMainMessage(currentTemplate.configuration.mainMessage);
    }
  }, [currentTemplate.configuration?.mainMessage]);

  const handleSaveMessage = () => {
    setCurrentTemplate({
      ...currentTemplate,
      configuration: { ...currentTemplate.configuration, mainMessage: editedMainMessage }
    });
    setSavedMainMessage(editedMainMessage);
    setIsEditingMessage(false);
  };

  const handleCancelEdit = () => {
    setEditedMainMessage(savedMainMessage || currentTemplate.configuration?.mainMessage || '');
    setIsEditingMessage(false);
  };

  const handleResetMessage = () => {
    const originalMessage = currentTemplate.configuration?.mainMessage || '';
    setEditedMainMessage(originalMessage);
    setCurrentTemplate({
      ...currentTemplate,
      configuration: { ...currentTemplate.configuration, mainMessage: originalMessage }
    });
    setSavedMainMessage(originalMessage);
  };

  const previewConfig = useMemo(() => currentTemplate.configuration
    ? { ...currentTemplate.configuration, mainMessage: editedMainMessage }
    : null, [currentTemplate.configuration, editedMainMessage]);

  const getButtonUrl = (config: any) => {
    const baseUrl = 'https://taginn.app';
    switch (config.buttonType) {
      case 'review':
        return config.selectedReviewId ? `${baseUrl}/review/${config.selectedReviewId}` : '#';
      case 'coupon':
        return config.selectedCouponId ? `${baseUrl}/coupon-offer/${config.selectedCouponId}` : '#';
      case 'loyalty': {
        const loyaltyProgram = loyaltyPrograms.find(p => p.id === config.selectedLoyaltyId);
        return loyaltyProgram?.template_code ? `${baseUrl}/get-pass?template=${loyaltyProgram.template_code}` : '#';
      }
      case 'custom':
        return config.ctaLink || '#';
      case 'none':
      default:
        return '#';
    }
  };

  const getButtonText = (config: any) => {
    if (config.ctaText?.trim()) return config.ctaText;
    switch (config.buttonType) {
      case 'review': {
        const reviewForm = reviewForms.find(f => f.id === config.selectedReviewId);
        return reviewForm ? `Leave Review` : 'Review';
      }
      case 'coupon': {
        const coupon = couponOffers.find(c => c.id === config.selectedCouponId);
        return coupon ? `Get ${coupon.name}` : 'Get Coupon';
      }
      case 'loyalty': {
        const loyalty = loyaltyPrograms.find(l => l.id === config.selectedLoyaltyId);
        return loyalty ? `Join ${loyalty.name}` : 'Join Loyalty';
      }
      case 'custom':
        return 'Call to Action';
      case 'none':
      default:
        return 'Call to Action';
    }
  };

  const shouldShowButton = (config: any) => config.buttonType && config.buttonType !== 'none';

  const clearValidationError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors(({ [fieldName]: _, ...rest }) => rest);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be smaller than 5MB'); return; }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setLogoPreview(dataUrl);
      setCurrentTemplate({ ...currentTemplate, configuration: { ...currentTemplate.configuration, logoUrl: dataUrl } });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setCurrentTemplate({ ...currentTemplate, configuration: { ...currentTemplate.configuration, logoUrl: '' } });
  };

  const handleCanvaZipUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) { toast.error('Please upload a ZIP file exported from Canva'); return; }
    if (!selectedBusinessId) { toast.error('Please select a business first'); return; }

    setIsImportingCanva(true);
    try {
      const result = await extractCanvaZip(file, selectedBusinessId);
      if (!result.success) { toast.error(result.error.message); return; }

      const parsed = parseCanvaHtml(result.data.html);
      setCanvaTextBlocks(parsed.textBlocks);
      setCanvaButtons(parsed.buttons);
      setCanvaHtmlPreview(result.data.html);
      setCanvaMappingDialogOpen(true);
    } catch {
      toast.error('Failed to import Canva template');
    } finally {
      setIsImportingCanva(false);
      if (canvaZipInputRef.current) canvaZipInputRef.current.value = '';
    }
  };

  const handleCanvaMappingConfirm = (mappings: CanvaTemplateMappings) => {
    try {
      const generated = generateCanvaTemplate(canvaHtmlPreview, canvaTextBlocks, canvaButtons, mappings);
      setCurrentTemplate({
        ...currentTemplate,
        configuration: {
          ...currentTemplate.configuration,
          html: generated.html,
          mainMessage: generated.mainMessage,
          importSource: 'canva',
          canvaBlocks: generated.canvaBlocks,
          preset: undefined,
        }
      });
      setIsAdvancedMode(true);
      setCanvaMappingDialogOpen(false);
      toast.success('Canva template imported successfully! Review and save your template.');
    } catch {
      toast.error('Failed to process template mappings');
    }
  };

  const uploadLogoToStorage = async (file: File): Promise<string> => {
    if (!user?.id) throw new Error('User not authenticated');
    
    const fileExt = file.name.split('.').pop();
    const timestamp = Date.now();
    const fileName = `${user.id}_email_logo_${timestamp}.${fileExt}`;

    try {
      // Upload to business-assets bucket
      const { error: uploadError } = await supabase.storage
        .from('business-assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('business-assets')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Logo upload failed:', error);
      throw new Error('Logo upload failed');
    }
  };

  const parseHTMLToConfig = (html: string) => {
    const config: any = {};

    try {
      // Extract header text
      const h1Match = html.match(/<h1[^>]*>(.*?)<\/h1>/s);
      if (h1Match) config.headerText = h1Match[1].trim();

      // Extract main message (content between <p> tags in .content or .message div)
      const contentPMatch = html.match(/<(?:div class="message"|p)[^>]*>(.*?)<\/(?:div|p)>/s);
      if (contentPMatch) {
        config.mainMessage = contentPMatch[1].replace(/<br\s*\/?>/g, '\n').trim();
      }

      // Extract footer text
      const footerPMatch = html.match(/<div class="footer"[^>]*>[\s\S]*?<p[^>]*>(.*?)<\/p>/s);
      if (footerPMatch) config.footerText = footerPMatch[1].trim();

      // Extract CTA button text
      const ctaMatch = html.match(/<a[^>]*class="cta-button"[^>]*>(.*?)<\/a>/s);
      if (ctaMatch) config.ctaText = ctaMatch[1].trim();

      // Extract primary color from .cta-button background style
      const colorMatch = html.match(/\.cta-button\s*\{[^}]*background:\s*([^;]+);/s);
      if (colorMatch) config.primaryColor = colorMatch[1].trim();

      // Extract font family from body style
      const fontMatch = html.match(/body\s*\{[^}]*font-family:\s*([^;]+);/s);
      if (fontMatch) config.fontFamily = fontMatch[1].trim();

      // Extract logo URL
      const logoMatch = html.match(/<img[^>]*src="([^"]*)"[^>]*class="logo"/);
      if (logoMatch) config.logoUrl = logoMatch[1];

      // Extract logo position from .header style
      const logoPositionMatch = html.match(/\.header\s*\{[^}]*text-align:\s*(left|center|right);/s);
      if (logoPositionMatch) config.logoPosition = logoPositionMatch[1];

    } catch (error) {
      console.error('Error parsing HTML:', error);
    }

    return config;
  };

  const generateHTML = (config: any) => {
    const preset = EMAIL_PRESETS.find(p => p.id === config.preset);

    // Process all dynamic content to avoid nested template literal issues
    const processedMainMessage = config.mainMessage ? config.mainMessage.replace(/\n/g, '<br>') : '';

    // Canva header image (full-width design from Canva)
    const canvaHeaderSection = config.canvaHeaderImage ? '<img src="' + config.canvaHeaderImage + '" alt="Email Design" style="width: 100%; height: auto; display: block;">' : '';

    // Logo section (only if no Canva header)
    const logoSection = !config.canvaHeaderImage && config.logoUrl ? '<img src="' + config.logoUrl + '" alt="Logo" class="logo">' : '';

    // Header text (only if no Canva header - header is in the Canva design)
    const headerSection = !config.canvaHeaderImage && config.headerText ? '<h1>' + config.headerText + '</h1>' : '';

    const mainMessageSection = processedMainMessage ? '<p>' + processedMainMessage + '</p>' : '';
    const buttonTextColor = getTextColorForBackground(config.primaryColor || '#1976d2');
    const buttonBgColor = config.primaryColor || '#1976d2';
    const ctaSection = shouldShowButton(config) ? '<a href="' + getButtonUrl(config) + '" class="cta-button" style="display: inline-block; background: ' + buttonBgColor + '; color: ' + buttonTextColor + '; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin-bottom: 24px; font-weight: 600;">' + getButtonText(config) + '</a>' : '';
    const footerSection = config.footerText ? '<p>' + config.footerText + '</p>' : '';

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${config.headerText || preset?.name || 'Email'}</title>
  <style>
    body {
      font-family: ${config.fontFamily || 'Arial, sans-serif'};
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 0;
      background-color: #ffffff;
    }
    .canva-header {
      width: 100%;
      padding: 0;
      margin: 0;
    }
    .canva-header img {
      width: 100%;
      height: auto;
      display: block;
    }
    .header {
      text-align: ${config.logoPosition || 'center'};
      padding: 24px 24px 8px;
    }
    .logo {
      max-width: 150px;
      height: auto;
    }
    .content {
      padding: 24px;
    }
    h1 {
      font-weight: bold;
      margin-bottom: 16px;
      color: #111827;
      font-size: 24px;
    }
    .message {
      color: #374151;
      margin-bottom: 24px;
      line-height: 1.625;
    }
    .cta-button {
      display: inline-block;
      background: ${config.primaryColor || '#1976d2'};
      color: ${buttonTextColor};
      padding: 12px 24px;
      border-radius: 8px;
      text-decoration: none;
      margin-bottom: 24px;
      font-weight: 600;
    }
    .cta-button:hover {
      opacity: 0.9;
    }
    .footer {
      background-color: #f9fafb;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      padding: 16px 24px;
      font-size: 14px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  ${config.preheaderText ? `<span style="display:none!important;visibility:hidden;mso-hide:all;font-size:1px;color:#ffffff;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">${config.preheaderText}</span>` : ''}
  ${canvaHeaderSection ? '<div class="canva-header">' + canvaHeaderSection + '</div>' : ''}
  ${!canvaHeaderSection ? '<div class="header">' + logoSection + '</div>' : ''}

  <div class="content">
    ${headerSection}
    <div class="message">
      ${mainMessageSection}
    </div>
    ${ctaSection}
  </div>

  <div class="footer">
    ${footerSection}
  </div>
</body>
</html>`;
  };

  const saveTemplate = async () => {
    const errors: {[key: string]: string} = {};

    // Clear previous errors
    setValidationErrors({});

    if (!user?.id || !currentTemplate.name?.trim()) {
      errors.templateName = 'Template name is required';
    }

    // Validate button configuration
    const config = currentTemplate.configuration;
    if (config?.buttonType && config.buttonType !== 'none') {
      switch (config.buttonType) {
        case 'review':
          if (!config.selectedReviewId) {
            errors.selectedReviewId = 'Please select a review form for the button';
          }
          break;
        case 'coupon':
          if (!config.selectedCouponId) {
            errors.selectedCouponId = 'Please select a coupon offer for the button';
          }
          break;
        case 'loyalty':
          if (!config.selectedLoyaltyId) {
            errors.selectedLoyaltyId = 'Please select a loyalty program for the button';
          }
          break;
        case 'custom':
          if (!config.ctaLink?.trim()) {
            errors.ctaLink = 'Please enter a button link for custom button';
          } else {
            // Basic URL validation
            try {
              new URL(config.ctaLink);
            } catch {
              errors.ctaLink = 'Please enter a valid URL for the button link';
            }
          }
          break;
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error('Please fix the validation errors before saving');
      return;
    }

    setLoading(true);
    try {
      let logoUrl = currentTemplate.configuration?.logoUrl || '';

      // Upload logo if there's a new file
      if (logoFile && logoPreview.startsWith('data:')) {
        try {
          logoUrl = await uploadLogoToStorage(logoFile);
        } catch (error) {
          toast.error('Failed to upload logo');
          setLoading(false);
          return;
        }
      }

      const templateData = {
        name: currentTemplate.name,
        configuration: isAdvancedMode
          ? {
              html: currentTemplate.configuration?.html,
              // Preserve Canva metadata when saving in advanced mode
              importSource: currentTemplate.configuration?.importSource,
              mainMessage: currentTemplate.configuration?.mainMessage,
              canvaBlocks: currentTemplate.configuration?.canvaBlocks,
            }
          : {
              ...currentTemplate.configuration,
              logoUrl: logoUrl,
              html: generateHTML({ ...currentTemplate.configuration, logoUrl })
            },
        user_id: user.id,
      };

      if (existingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('email_templates')
          .update(templateData)
          .eq('id', existingTemplate.id);

        if (error) throw error;
        toast.success('Template updated successfully!');
      } else {
        // Create new template
        const { error } = await supabase
          .from('email_templates')
          .insert([templateData]);

        if (error) throw error;
        toast.success('Template saved successfully!');
      }

      // Clear draft after successful save
      clearDraft();

      onComplete(); // Call onComplete callback
    } catch (error) {
      toast.error(`Failed to ${existingTemplate ? 'update' : 'save'} template`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const sendTestEmail = async () => {
    if (!testEmail || !currentTemplate.name || !user?.id) {
      toast.error('Please enter your email and save the template first');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingTest(true);
    try {
      // Get business sender information
      let senderName = '';

      if (selectedBusinessId) {
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('sender_name, name')
          .eq('id', selectedBusinessId)
          .single();

        if (!businessError && businessData) {
          senderName = businessData.sender_name || businessData.name || 'Your Business';
        }
      }

      // Generate HTML content
      let emailHtml = '';
      if (isAdvancedMode && currentTemplate.configuration?.html) {
        emailHtml = currentTemplate.configuration.html;
      } else {
        emailHtml = generateHTML(currentTemplate.configuration);
      }

      const requestPayload = {
        email: testEmail,
        subject: currentTemplate.configuration?.headerText || 'Email Template Test',
        htmlContent: emailHtml,
        senderName: senderName,
        businessId: selectedBusinessId
      };

      console.log('Generated email HTML length:', emailHtml.length);
      console.log('Request payload:', {
        email: requestPayload.email,
        subject: requestPayload.subject,
        htmlContentLength: requestPayload.htmlContent.length,
        htmlContentPreview: requestPayload.htmlContent.substring(0, 100) + '...'
      });

      // Make a direct fetch call to get better error details
      const { data: { session } } = await supabase.auth.getSession();

      const fetchResponse = await fetch(`https://mtzgsiyrjoqihggjrmqv.supabase.co/functions/v1/send-test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('Fetch response status:', fetchResponse.status);
      console.log('Fetch response ok:', fetchResponse.ok);

      const responseText = await fetchResponse.text();
      console.log('Response text:', responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('Parsed response data:', responseData);

        if (responseData.debug) {
          console.log('🔍 DEBUG INFO:', responseData.debug);
        }
      } catch (parseError) {
        console.log('Could not parse response as JSON:', parseError);
      }

      if (!fetchResponse.ok) {
        if (responseData && responseData.error) {
          throw new Error(`Email sending failed: ${responseData.error}`);
        } else {
          throw new Error(`Email sending failed: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }
      }

      toast.success('Test email sent successfully! Check your inbox.');
      setTestEmail('');
    } catch (error) {
      console.error('Full error details:', error);
      // For now, show a simpler message to users
      toast.error('Failed to send test email. Please check your email address and try again.');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="p-6 space-y-6 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">
            {existingTemplate ? 'Edit Email Template' : 'Create Email Template'}
          </h2>
          <p className="text-gray-600 mt-1">
            {existingTemplate ? 'Update your email template' : 'Create professional email templates for your business'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Hidden file input for Canva ZIP upload */}
          <input
            ref={canvaZipInputRef}
            type="file"
            accept=".zip"
            onChange={handleCanvaZipUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => canvaZipInputRef.current?.click()}
            disabled={isImportingCanva}
            className="flex items-center gap-2"
          >
            {isImportingCanva ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileArchive className="h-4 w-4" />
            )}
            {isImportingCanva ? 'Importing...' : 'Import from Canva'}
          </Button>
          <Button
            variant={isAdvancedMode ? "destructive" : "outline"}
            onClick={handleModeToggle}
            className="flex items-center gap-2"
          >
            <Code className="h-4 w-4" />
            {isAdvancedMode ? 'Switch to Simple Mode' : 'Use Custom HTML'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[80vh] w-full">
        {/* Left Panel - All Builder Components (50% width) */}
        <div className="space-y-4 overflow-y-auto pr-4 w-full">
          {/* Email Builder */}
          <Card className="w-full">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {isAdvancedMode ? 'HTML Editor' : 'Email Builder'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    clearDraft();
                    onCancel();
                  }}
                  className="h-8 w-8 -mt-1 -mr-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className={isAdvancedMode ? "max-w-none" : ""}>
              {isAdvancedMode ? (
                <div className="space-y-4">
                  <Label>Custom HTML</Label>
                  <CodeEditor
                    value={currentTemplate.configuration?.html || ''}
                    onChange={(newHtml) => {
                      const parsedConfig = parseHTMLToConfig(newHtml);

                      setCurrentTemplate({
                        ...currentTemplate,
                        configuration: {
                          ...currentTemplate.configuration,
                          ...parsedConfig,
                          html: newHtml
                        }
                      });
                    }}
                    placeholder="Enter your custom HTML here..."
                    minHeight="400px"
                  />
                </div>
              ) : (
                <Tabs defaultValue="template" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="template" className="flex items-center gap-1">
                      <Layout className="h-4 w-4" />
                      Preset
                    </TabsTrigger>
                    <TabsTrigger value="design" className="flex items-center gap-1">
                      <Palette className="h-4 w-4" />
                      Design
                    </TabsTrigger>
                    <TabsTrigger value="content" className="flex items-center gap-1">
                      <Type className="h-4 w-4" />
                      Content
                    </TabsTrigger>
                    <TabsTrigger value="branding" className="flex items-center gap-1">
                      <Image className="h-4 w-4" />
                      Branding
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="template" className="space-y-4 mt-4">
                    <Label>Choose Preset</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {EMAIL_PRESETS.map((preset) => (
                        <Card 
                          key={preset.id}
                          className={'cursor-pointer transition-all hover:shadow-md ' + (
                            currentTemplate.configuration?.preset === preset.id ? 'ring-2 ring-blue-500' : ''
                          )}
                          onClick={() => {
                            // Determine button type based on preset
                            let buttonType: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none' = 'custom';
                            if (preset.id === 'loyalty') buttonType = 'loyalty';
                            else if (preset.id === 'coupon-reward') buttonType = 'coupon';
                            else if (preset.id === 'welcome') buttonType = 'review'; // Welcome emails often lead to reviews
                            else buttonType = 'custom'; // event-invitation, newsletter, promotion
                            
                            setCurrentTemplate({
                              ...currentTemplate,
                              configuration: {
                                ...currentTemplate.configuration,
                                preset: preset.id,
                                headerText: getPresetContent(preset.id).headerText,
                                mainMessage: getPresetContent(preset.id).mainMessage,
                                ctaText: getPresetContent(preset.id).ctaText,
                                footerText: getPresetContent(preset.id).footerText,
                                buttonType: buttonType
                              }
                            });
                          }}
                        >
                          <CardContent className="p-4">
                            <h4 className="font-medium">{preset.name}</h4>
                            <p className="text-sm text-gray-600">{preset.description}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="design" className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="primaryColor">Primary Color</Label>
                      <Input
                        id="primaryColor"
                        type="color"
                        value={currentTemplate.configuration?.primaryColor || '#1976d2'}
                        onChange={(e) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            primaryColor: e.target.value
                          }
                        })}
                      />
                    </div>
                    <div>
                      <Label>Font Family</Label>
                      <Select
                        value={currentTemplate.configuration?.fontFamily || 'Arial, sans-serif'}
                        onValueChange={(value) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            fontFamily: value
                          }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>

                  <TabsContent value="content" className="space-y-4 mt-4">
                    <div>
                      <Label>Header Text</Label>
                      <Input
                        value={currentTemplate.configuration?.headerText || ''}
                        onChange={(e) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            headerText: e.target.value
                          }
                        })}
                        placeholder="Email headline"
                      />
                    </div>
                    <div>
                      <Label>Preview Text (Preheader)</Label>
                      <Input
                        value={currentTemplate.configuration?.preheaderText || ''}
                        onChange={(e) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            preheaderText: e.target.value
                          }
                        })}
                        placeholder="Text shown before opening email in inbox"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        This text appears after the subject line in most email clients
                      </p>
                    </div>
                    <div>
                      <Label>Main Message</Label>
                      <div className="flex gap-2 mt-1">
                        {!isEditingMessage ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Initialize with template message if not already set
                                if (!editedMainMessage && currentTemplate.configuration?.mainMessage) {
                                  setEditedMainMessage(currentTemplate.configuration.mainMessage);
                                  setSavedMainMessage(currentTemplate.configuration.mainMessage);
                                }
                                setIsEditingMessage(true);
                              }}
                              className="gap-2"
                            >
                              <Pencil className="h-4 w-4" />
                              Edit Message
                            </Button>
                            {editedMainMessage !== currentTemplate.configuration?.mainMessage && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleResetMessage}
                                className="gap-2"
                              >
                                <X className="h-4 w-4" />
                                Reset
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button onClick={handleSaveMessage} size="sm">
                              Save Changes
                            </Button>
                            <Button onClick={handleCancelEdit} variant="outline" size="sm">
                              Cancel
                            </Button>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Click "Edit Message" to edit the text directly in the preview panel →
                      </p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Button Type</Label>
                        <Select
                          value={currentTemplate.configuration?.buttonType || 'custom'}
                          onValueChange={(value: 'review' | 'coupon' | 'loyalty' | 'custom' | 'none') => setCurrentTemplate({
                            ...currentTemplate,
                            configuration: {
                              ...currentTemplate.configuration,
                              buttonType: value,
                              // Clear selections when changing type
                              selectedReviewId: value === 'review' ? currentTemplate.configuration?.selectedReviewId : undefined,
                              selectedCouponId: value === 'coupon' ? currentTemplate.configuration?.selectedCouponId : undefined,
                              selectedLoyaltyId: value === 'loyalty' ? currentTemplate.configuration?.selectedLoyaltyId : undefined,
                              // Auto-populate button text if empty, preserve if already set
                              ctaText: value === 'none' ? '' : 
                                currentTemplate.configuration?.ctaText || 
                                (value === 'review' ? 'Leave a Review' : 
                                 value === 'coupon' ? 'Get Coupon' : 
                                 value === 'loyalty' ? 'Join Program' : 
                                 value === 'custom' ? 'Learn More' : ''),
                              ctaLink: value === 'custom' ? currentTemplate.configuration?.ctaLink : ''
                            }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select button type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Button</SelectItem>
                            <SelectItem value="review">Review Form</SelectItem>
                            <SelectItem value="coupon">Coupon Offer</SelectItem>
                            <SelectItem value="loyalty">Loyalty Program</SelectItem>
                            <SelectItem value="custom">Custom Link</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Review Form Dropdown + Button Text */}
                      {currentTemplate.configuration?.buttonType === 'review' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Select Review Form</Label>
                            <Select
                              value={currentTemplate.configuration?.selectedReviewId || ''}
                              onValueChange={(value) => {
                                setCurrentTemplate({
                                  ...currentTemplate,
                                  configuration: {
                                    ...currentTemplate.configuration,
                                    selectedReviewId: value
                                  }
                                });
                                clearValidationError('selectedReviewId');
                              }}
                            >
                              <SelectTrigger className={validationErrors.selectedReviewId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}>
                                <SelectValue placeholder="Choose a review form" />
                              </SelectTrigger>
                              <SelectContent>
                                {reviewForms.map((form) => (
                                  <SelectItem key={form.id} value={form.id}>
                                    {form.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {validationErrors.selectedReviewId && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.selectedReviewId}</p>
                            )}
                          </div>
                          <div>
                            <Label>Button Text</Label>
                            <Input
                              value={currentTemplate.configuration?.ctaText || ''}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                configuration: {
                                  ...currentTemplate.configuration,
                                  ctaText: e.target.value
                                }
                              })}
                              placeholder="e.g., Leave a Review"
                            />
                          </div>
                        </div>
                      )}

                      {/* Coupon Offer Dropdown + Button Text */}
                      {currentTemplate.configuration?.buttonType === 'coupon' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Select Coupon Offer</Label>
                            <Select
                              value={currentTemplate.configuration?.selectedCouponId || ''}
                              onValueChange={(value) => {
                                setCurrentTemplate({
                                  ...currentTemplate,
                                  configuration: {
                                    ...currentTemplate.configuration,
                                    selectedCouponId: value
                                  }
                                });
                                clearValidationError('selectedCouponId');
                              }}
                            >
                              <SelectTrigger className={validationErrors.selectedCouponId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}>
                                <SelectValue placeholder="Choose a coupon offer" />
                              </SelectTrigger>
                              <SelectContent>
                                {couponOffers.filter(offer => offer.is_active).map((offer) => (
                                  <SelectItem key={offer.id} value={offer.id}>
                                    {offer.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {validationErrors.selectedCouponId && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.selectedCouponId}</p>
                            )}
                          </div>
                          <div>
                            <Label>Button Text</Label>
                            <Input
                              value={currentTemplate.configuration?.ctaText || ''}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                configuration: {
                                  ...currentTemplate.configuration,
                                  ctaText: e.target.value
                                }
                              })}
                              placeholder="e.g., Get Coupon"
                            />
                          </div>
                        </div>
                      )}

                      {/* Loyalty Program Dropdown + Button Text */}
                      {currentTemplate.configuration?.buttonType === 'loyalty' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Select Loyalty Program</Label>
                            <Select
                              value={currentTemplate.configuration?.selectedLoyaltyId || ''}
                              onValueChange={(value) => {
                                setCurrentTemplate({
                                  ...currentTemplate,
                                  configuration: {
                                    ...currentTemplate.configuration,
                                    selectedLoyaltyId: value
                                  }
                                });
                                clearValidationError('selectedLoyaltyId');
                              }}
                            >
                              <SelectTrigger className={validationErrors.selectedLoyaltyId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}>
                                <SelectValue placeholder="Choose a loyalty program" />
                              </SelectTrigger>
                              <SelectContent>
                                {loyaltyPrograms.filter(program => program.is_active).map((program) => (
                                  <SelectItem key={program.id} value={program.id}>
                                    {program.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {validationErrors.selectedLoyaltyId && (
                              <p className="text-xs text-red-500 mt-1">{validationErrors.selectedLoyaltyId}</p>
                            )}
                          </div>
                          <div>
                            <Label>Button Text</Label>
                            <Input
                              value={currentTemplate.configuration?.ctaText || ''}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                configuration: {
                                  ...currentTemplate.configuration,
                                  ctaText: e.target.value
                                }
                              })}
                              placeholder="e.g., Join Program"
                            />
                          </div>
                        </div>
                      )}

                      {/* Button Text Field - for custom button type only */}
                      {currentTemplate.configuration?.buttonType === 'custom' && (
                        <div>
                          <Label>Button Text</Label>
                          <Input
                            value={currentTemplate.configuration?.ctaText || ''}
                            onChange={(e) => setCurrentTemplate({
                              ...currentTemplate,
                              configuration: {
                                ...currentTemplate.configuration,
                                ctaText: e.target.value
                              }
                            })}
                            placeholder="Call to action text"
                          />
                        </div>
                      )}

                      {/* Custom Button Link Field */}
                      {currentTemplate.configuration?.buttonType === 'custom' && (
                        <div>
                          <Label>Button Link</Label>
                          <Input
                            value={currentTemplate.configuration?.ctaLink || ''}
                            onChange={(e) => {
                              setCurrentTemplate({
                                ...currentTemplate,
                                configuration: {
                                  ...currentTemplate.configuration,
                                  ctaLink: e.target.value
                                }
                              });
                              clearValidationError('ctaLink');
                            }}
                            placeholder="https://your-link.com"
                            className={validationErrors.ctaLink ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                          />
                          {validationErrors.ctaLink && (
                            <p className="text-xs text-red-500 mt-1">{validationErrors.ctaLink}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Footer Text</Label>
                      <Input
                        value={currentTemplate.configuration?.footerText || ''}
                        onChange={(e) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            footerText: e.target.value
                          }
                        })}
                        placeholder="Footer information"
                      />
                    </div>
                    <div>
                      <Label>Unsubscribe Link Text</Label>
                      <Input
                        value={currentTemplate.configuration?.unsubscribeText || ''}
                        onChange={(e) => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            unsubscribeText: e.target.value
                          }
                        })}
                        placeholder="Unsubscribe"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This text will appear as the unsubscribe link in your emails
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="branding" className="space-y-4 mt-4">
                    <div>
                      <Label>Business Logo</Label>
                      <div className="space-y-3">
                        {logoPreview || currentTemplate.configuration?.logoUrl ? (
                          <div className="relative">
                            <img 
                              src={logoPreview || currentTemplate.configuration?.logoUrl} 
                              alt="Logo preview" 
                              className="w-32 h-32 object-contain border rounded-lg bg-gray-50"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                              onClick={removeLogo}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                            onClick={() => document.getElementById('logo-upload')?.click()}
                          >
                            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 mb-1">Click to upload business logo</p>
                            <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                          </div>
                        )}
                        <input
                          id="logo-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Logo Position</Label>
                      <Select
                        value={currentTemplate.configuration?.logoPosition || 'center'}
                        onValueChange={(value: 'left' | 'center' | 'right') => setCurrentTemplate({
                          ...currentTemplate,
                          configuration: {
                            ...currentTemplate.configuration,
                            logoPosition: value
                          }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Left</SelectItem>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="right">Right</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </Tabs>
              )}

              {/* Template Management - moved into builder */}
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Template Name</Label>
                  <Input
                    value={currentTemplate.name || ''}
                    onChange={(e) => {
                      setCurrentTemplate({
                        ...currentTemplate,
                        name: e.target.value
                      });
                      clearValidationError('templateName');
                    }}
                    placeholder="Enter template name"
                    className={validationErrors.templateName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                  />
                  {validationErrors.templateName && (
                    <p className="text-xs text-red-500 mt-1">{validationErrors.templateName}</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveTemplate} disabled={loading} className="flex-1">
                    {loading ? 'Saving...' : (existingTemplate ? 'Update Template' : 'Save Template')}
                    <Save className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      clearDraft();
                      onCancel();
                    }}
                    disabled={loading}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Send Test Email Section */}
          <Card>
            <CardHeader>
              <CardTitle>Send Test Email</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your email address to receive a test email preview
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={sendingTest}
                  className="flex-1"
                />
                <Button
                  onClick={sendTestEmail}
                  disabled={!testEmail || sendingTest}
                  size="sm"
                >
                  {sendingTest ? "Sending..." : "Send Test"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Preview Only (50% width) - Responsive */}
        <div className="space-y-4">
          {/* Preview Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Preview</h3>
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
                className="px-2 py-1 h-8"
              >
                <Monitor className="h-3 w-3" />
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
                className="px-2 py-1 h-8"
              >
                <Smartphone className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="transition-all duration-300 w-full flex justify-center">
            <EmailTemplatePreview
              configuration={previewConfig || currentTemplate.configuration || {}}
              mode={previewMode}
              isAdvancedMode={false}
              getButtonUrl={getButtonUrl}
              getButtonText={getButtonText}
              shouldShowButton={shouldShowButton}
              logoPreview={logoPreview}
              isEditingMessage={isEditingMessage}
              editedMainMessage={editedMainMessage}
              onMessageChange={setEditedMainMessage}
            />
          </div>
        </div>
      </div>

      {/* Canva Template Mapping Dialog */}
      <CanvaTemplateMappingDialog
        open={canvaMappingDialogOpen}
        onOpenChange={setCanvaMappingDialogOpen}
        textBlocks={canvaTextBlocks}
        buttons={canvaButtons}
        htmlPreview={canvaHtmlPreview}
        businessId={selectedBusinessId || ''}
        onConfirm={handleCanvaMappingConfirm}
        onCancel={() => setCanvaMappingDialogOpen(false)}
      />
    </div>
  );
};

export default EmailTemplateBuilder;